import React, { createContext, useState, useContext, ReactNode, useCallback, useMemo, useEffect } from 'react';
import { usePerformanceMode } from '../hooks/usePerformanceMode';

type Theme = 'system' | 'light' | 'dark';

// Helper function to get initial state from localStorage
const getInitialState = <T,>(key: string, defaultValue: T): T => {
    if (typeof window === 'undefined') {
        return defaultValue;
    }
    try {
        const item = window.localStorage.getItem(key);
        return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
        console.warn(`Error reading localStorage key "${key}":`, error);
        return defaultValue;
    }
};

interface ISettingsContext {
    theme: Theme;
    resolvedTheme: 'light' | 'dark';
    isBackgroundAnimationEnabled: boolean;
    isRevealAnimationEnabled: boolean;
    isMagneticLogoEnabled: boolean;
    isFabParticlesEnabled: boolean;
    isCardGlowEnabled: boolean;
    isHighQualityBlursEnabled: boolean;
    isAutoOptimizerEnabled: boolean;
    setTheme: (theme: Theme) => void;
    toggleBackgroundAnimation: () => void;
    toggleRevealAnimation: () => void;
    toggleMagneticLogo: () => void;
    toggleFabParticles: () => void;
    toggleCardGlow: () => void;
    toggleHighQualityBlurs: () => void;
    toggleAutoOptimizer: () => void;
    resetSettings: () => void;
}

const SettingsContext = createContext<ISettingsContext | undefined>(undefined);

const DEFAULTS = {
    theme: 'system' as Theme,
    backgroundAnimation: true,
    revealAnimation: true,
    magneticLogo: true,
    fabParticles: true,
    cardGlow: true,
    highQualityBlurs: true,
};

export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [theme, setThemeState] = useState<Theme>(DEFAULTS.theme);
    const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('dark');
    
    // User preference states (saved in localStorage)
    const [isBackgroundAnimationEnabledPref, setIsBackgroundAnimationEnabledPref] = useState<boolean>(DEFAULTS.backgroundAnimation);
    const [isRevealAnimationEnabledPref, setIsRevealAnimationEnabledPref] = useState<boolean>(DEFAULTS.revealAnimation);
    const [isMagneticLogoEnabledPref, setIsMagneticLogoEnabledPref] = useState<boolean>(DEFAULTS.magneticLogo);
    const [isFabParticlesEnabledPref, setIsFabParticlesEnabledPref] = useState<boolean>(DEFAULTS.fabParticles);
    const [isCardGlowEnabledPref, setIsCardGlowEnabledPref] = useState<boolean>(DEFAULTS.cardGlow);
    const [isHighQualityBlursEnabledPref, setIsHighQualityBlursEnabledPref] = useState<boolean>(DEFAULTS.highQualityBlurs);
    const [isAutoOptimizerEnabled, setIsAutoOptimizerEnabled] = useState<boolean>(false);
    
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    
    const { isLowSpec, isLoading } = usePerformanceMode();

    // Load settings from localStorage on client-side mount
    useEffect(() => {
        if (isLoading || isLoaded) return;
        setThemeState(getInitialState('settings:theme', DEFAULTS.theme));
        setIsBackgroundAnimationEnabledPref(getInitialState('settings:backgroundAnimation', DEFAULTS.backgroundAnimation));
        setIsRevealAnimationEnabledPref(getInitialState('settings:revealAnimation', DEFAULTS.revealAnimation));
        setIsMagneticLogoEnabledPref(getInitialState('settings:magneticLogo', DEFAULTS.magneticLogo));
        setIsFabParticlesEnabledPref(getInitialState('settings:fabParticles', DEFAULTS.fabParticles));
        setIsCardGlowEnabledPref(getInitialState('settings:cardGlow', DEFAULTS.cardGlow));
        setIsHighQualityBlursEnabledPref(getInitialState('settings:highQualityBlurs', DEFAULTS.highQualityBlurs));
        // Default to isLowSpec if not set in localStorage
        setIsAutoOptimizerEnabled(getInitialState('settings:autoOptimizer', isLowSpec));
        setIsLoaded(true);

        // Cleanup redundant old cookies to free up request header space and optimize latency
        try {
            const keys = [
                'settings:theme',
                'settings:backgroundAnimation',
                'settings:revealAnimation',
                'settings:magneticLogo',
                'settings:fabParticles',
                'settings:cardGlow',
                'settings:highQualityBlurs',
                'settings:autoOptimizer'
            ];
            keys.forEach(key => {
                document.cookie = `${key}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
            });
        } catch (error) {
            console.warn('Cookie cleanup failed:', error);
        }
    }, [isLoading, isLowSpec, isLoaded]);

    // Calculate effective settings (if Auto-Optimizer is enabled, override to false)
    const isOptimized = isAutoOptimizerEnabled;

    const isBackgroundAnimationEnabled = isOptimized ? false : isBackgroundAnimationEnabledPref;
    const isRevealAnimationEnabled = isOptimized ? false : isRevealAnimationEnabledPref;
    const isMagneticLogoEnabled = isOptimized ? false : isMagneticLogoEnabledPref;
    const isFabParticlesEnabled = isOptimized ? false : isFabParticlesEnabledPref;
    const isCardGlowEnabled = isOptimized ? false : isCardGlowEnabledPref;
    const isHighQualityBlursEnabled = isOptimized ? false : isHighQualityBlursEnabledPref;

    // Persist settings preferences to localStorage whenever they change (only after initial load)
    useEffect(() => {
        if (!isLoaded) return;
        try { 
            window.localStorage.setItem('settings:theme', JSON.stringify(theme));
        } 
        catch (error) { console.warn('Error setting localStorage for theme:', error); }
    }, [theme, isLoaded]);

    useEffect(() => {
        if (!isLoaded) return;
        try { 
            window.localStorage.setItem('settings:backgroundAnimation', JSON.stringify(isBackgroundAnimationEnabledPref));
        }
        catch (error) { console.warn('Error setting localStorage for backgroundAnimation:', error); }
    }, [isBackgroundAnimationEnabledPref, isLoaded]);

    useEffect(() => {
        if (!isLoaded) return;
        try { 
            window.localStorage.setItem('settings:revealAnimation', JSON.stringify(isRevealAnimationEnabledPref));
        }
        catch (error) { console.warn('Error setting localStorage for reveal animation:', error); }
    }, [isRevealAnimationEnabledPref, isLoaded]);
    
    useEffect(() => {
        if (!isLoaded) return;
        try { 
            window.localStorage.setItem('settings:magneticLogo', JSON.stringify(isMagneticLogoEnabledPref));
        }
        catch (error) { console.warn('Error setting localStorage for magnetic logo:', error); }
    }, [isMagneticLogoEnabledPref, isLoaded]);

    useEffect(() => {
        if (!isLoaded) return;
        try { 
            window.localStorage.setItem('settings:fabParticles', JSON.stringify(isFabParticlesEnabledPref));
        }
        catch (error) { console.warn('Error setting localStorage for FAB particles:', error); }
    }, [isFabParticlesEnabledPref, isLoaded]);

    useEffect(() => {
        if (!isLoaded) return;
        try { 
            window.localStorage.setItem('settings:cardGlow', JSON.stringify(isCardGlowEnabledPref));
        }
        catch (error) { console.warn('Error setting localStorage for card glow:', error); }
    }, [isCardGlowEnabledPref, isLoaded]);

    useEffect(() => {
        if (!isLoaded) return;
        try { 
            window.localStorage.setItem('settings:highQualityBlurs', JSON.stringify(isHighQualityBlursEnabledPref));
        }
        catch (error) { console.warn('Error setting localStorage for high quality blurs:', error); }
    }, [isHighQualityBlursEnabledPref, isLoaded]);

    useEffect(() => {
        if (!isLoaded) return;
        try { 
            window.localStorage.setItem('settings:autoOptimizer', JSON.stringify(isAutoOptimizerEnabled));
        }
        catch (error) { console.warn('Error setting localStorage for hardware auto-optimizer:', error); }
    }, [isAutoOptimizerEnabled, isLoaded]);

    // Sync resolved theme based on selected theme and system preferences
    useEffect(() => {
        const updateResolvedTheme = () => {
            if (theme === 'system') {
                const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
                setResolvedTheme(systemTheme);
            } else {
                setResolvedTheme(theme);
            }
        };

        updateResolvedTheme();

        if (theme === 'system') {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            const handleChange = () => updateResolvedTheme();

            if (mediaQuery.addEventListener) {
                mediaQuery.addEventListener('change', handleChange);
            } else {
                mediaQuery.addListener(handleChange);
            }

            return () => {
                if (mediaQuery.removeEventListener) {
                    mediaQuery.removeEventListener('change', handleChange);
                } else {
                    mediaQuery.removeListener(handleChange);
                }
            };
        }
    }, [theme]);

    // Apply resolved theme, blurs, and animation classes to <html> element
    useEffect(() => {
        const root = window.document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add(resolvedTheme);
    }, [resolvedTheme]);

    useEffect(() => {
        const root = window.document.documentElement;
        
        if (!isHighQualityBlursEnabled) {
            root.classList.add('no-blurs');
        } else {
            root.classList.remove('no-blurs');
        }

        if (!isBackgroundAnimationEnabled) {
            root.classList.add('no-bg-animation');
        } else {
            root.classList.remove('no-bg-animation');
        }

        if (!isCardGlowEnabled) {
            root.classList.add('no-card-glow');
        } else {
            root.classList.remove('no-card-glow');
        }
    }, [isHighQualityBlursEnabled, isBackgroundAnimationEnabled, isCardGlowEnabled]);


    const setTheme = useCallback((newTheme: Theme) => setThemeState(newTheme), []);
    const toggleBackgroundAnimation = useCallback(() => setIsBackgroundAnimationEnabledPref(prev => !prev), []);
    const toggleRevealAnimation = useCallback(() => setIsRevealAnimationEnabledPref(prev => !prev), []);
    const toggleMagneticLogo = useCallback(() => setIsMagneticLogoEnabledPref(prev => !prev), []);
    const toggleFabParticles = useCallback(() => setIsFabParticlesEnabledPref(prev => !prev), []);
    const toggleCardGlow = useCallback(() => setIsCardGlowEnabledPref(prev => !prev), []);
    const toggleHighQualityBlurs = useCallback(() => setIsHighQualityBlursEnabledPref(prev => !prev), []);
    const toggleAutoOptimizer = useCallback(() => setIsAutoOptimizerEnabled(prev => !prev), []);

    const resetSettings = useCallback(() => {
        setThemeState(DEFAULTS.theme);
        setIsBackgroundAnimationEnabledPref(DEFAULTS.backgroundAnimation);
        setIsRevealAnimationEnabledPref(DEFAULTS.revealAnimation);
        setIsMagneticLogoEnabledPref(DEFAULTS.magneticLogo);
        setIsFabParticlesEnabledPref(DEFAULTS.fabParticles);
        setIsCardGlowEnabledPref(DEFAULTS.cardGlow);
        setIsHighQualityBlursEnabledPref(DEFAULTS.highQualityBlurs);
        setIsAutoOptimizerEnabled(isLowSpec);
    }, [isLowSpec]);

    const value = useMemo(() => ({
        theme,
        resolvedTheme,
        isBackgroundAnimationEnabled,
        isRevealAnimationEnabled,
        isMagneticLogoEnabled,
        isFabParticlesEnabled,
        isCardGlowEnabled,
        isHighQualityBlursEnabled,
        isAutoOptimizerEnabled,
        setTheme,
        toggleBackgroundAnimation,
        toggleRevealAnimation,
        toggleMagneticLogo,
        toggleFabParticles,
        toggleCardGlow,
        toggleHighQualityBlurs,
        toggleAutoOptimizer,
        resetSettings,
    }), [
        theme, resolvedTheme, isBackgroundAnimationEnabled, isRevealAnimationEnabled, isMagneticLogoEnabled,
        isFabParticlesEnabled, isCardGlowEnabled, isHighQualityBlursEnabled, isAutoOptimizerEnabled,
        setTheme, toggleBackgroundAnimation, toggleRevealAnimation, toggleMagneticLogo,
        toggleFabParticles, toggleCardGlow, toggleHighQualityBlurs, toggleAutoOptimizer, resetSettings
    ]);

    return (
        <SettingsContext.Provider value={value}>
            {children}
        </SettingsContext.Provider>
    );
};

export const useSettings = (): ISettingsContext => {
    const context = useContext(SettingsContext);
    if (context === undefined) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};